import { useNavigate } from "react-router";
import { useState } from "react";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { SortOrder } from "primereact/api";
import { Button } from "@headlessui/react";
import { PencilIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useAppSelector } from "../../app/hooks";
import SimplePagination from "../../components/SimplePagination";
import SearchFilter from "../../components/input/SearchFilter";
import { CustomSortIcon } from "../../components/DatatableHelpers";
import { Loading } from "../../components/Loading";
import { ClassLinkOrCleverIcon } from "../../components/ClassLinkOrCleverIcon";
import { DistrictRowSpaViewModel, useGetDistrictListQuery } from "./districtsApiSlice";
import AddDistrict from "./addEdit/add/AddDistrict";

export default function Districts() {
	const navigate = useNavigate();
	const { config } = useAppSelector((state) => state.site);

	const [searchFilter, setSearchFilter] = useState("");
	const [page, setPage] = useState(0);
	const [sortField, setSortField] = useState("districtName");
	const [sortOrder, setSortOrder] = useState<SortOrder>(1);
	const [addDistrictFormIsOpen, setAddDistrictFormIsOpen] = useState(false);
	const districts = useGetDistrictListQuery().data;

	// Filtering & Sorting
	const filteredDistricts =
		districts?.filter((district) =>
			district.districtName?.toLowerCase().includes(searchFilter.toLowerCase())
		) ?? [];

	const sortedDistricts = [...filteredDistricts].sort((a, b) => {
		if (!sortField || sortOrder === 0) return 0;

		const normalize = (val: string | null | undefined) => val ?? "";

		// Special handling for Location sorting: sort by state first, then by city as tiebreaker
		if (sortField === "location") {
			const aState = normalize(a.state);
			const bState = normalize(b.state);
			if (aState !== bState) {
				return aState.localeCompare(bState) * sortOrder;
			}
			const aCity = normalize(a.city);
			const bCity = normalize(b.city);
			return aCity.localeCompare(bCity) * sortOrder;
		}

		// Special handling for Primary Contact sorting: sort by email first, then by phone as tiebreaker
		if (sortField === "contact") {
			const aEmail = normalize(a.emailAddress);
			const bEmail = normalize(b.emailAddress);
			if (aEmail !== bEmail) {
				return aEmail.localeCompare(bEmail) * sortOrder;
			}
			const aPhone = normalize(a.phone);
			const bPhone = normalize(b.phone);
			return aPhone.localeCompare(bPhone) * sortOrder;
		}

		// Single field sorting
		const aValue = a[sortField as keyof DistrictRowSpaViewModel];
		const bValue = b[sortField as keyof DistrictRowSpaViewModel];

		if (aValue == null && bValue == null) return 0;
		if (aValue == null) return sortOrder;
		if (bValue == null) return -sortOrder;

		if (aValue < bValue) return -sortOrder;
		if (aValue > bValue) return sortOrder;
		return 0;
	});

	const handleSort = (event: DataTableStateEvent) => {
		setSortField(event.sortField);
		setSortOrder(event.sortOrder ?? 0);
		setPage(0);
	};

	// Table
	const hasClassLinkOrCleverSchool = districts?.some((d) => d.cleverID || d.classLinkID) ?? false;

	const cleverOrClassLinkBodyTemplate = (row: DistrictRowSpaViewModel) => {
		return (
			<ClassLinkOrCleverIcon
				isClassLinkEntity={!!row.classLinkID}
				isCleverEntity={!!row.cleverID}
			/>
		);
	};

	const locationBodyTemplate = (row: DistrictRowSpaViewModel) => {
		return (
			<span>
				<span>
					<span className="font-medium!">State:</span> {row.state ?? "-"}
				</span>
				<div className="mt-1 text-gray-500 text-sm">
					<span className="font-medium!">City:</span> {row.city ?? "-"}
				</div>
			</span>
		);
	};

	const contactBodyTemplate = (row: DistrictRowSpaViewModel) => {
		return (
			<span>
				<span>
					<span className="font-medium!">Email:</span> {row.emailAddress ?? "-"}
				</span>
				<div className="mt-1 text-gray-500 text-sm">
					<span className="font-medium!">Phone:</span> {row.phone ?? "-"}
				</div>
			</span>
		);
	};

	const editBodyTemplate = (row: DistrictRowSpaViewModel) => {
		return (
			<button
				id={`edit-${row.districtID}-button`}
				className="mr-2 cursor-pointer"
				onClick={() => navigate(`/districts/edit?districtId=${row.districtID}`)}>
				<PencilIcon className="size-5" />
			</button>
		);
	};

	// Pagination
	const pageSize = 10;
	const paginatedDistricts = sortedDistricts.slice(page * pageSize, (page + 1) * pageSize);
	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	return (
		<>
			<section className="text-s sm:text-left w-full">
				<h1>Districts</h1>
			</section>
			<section className="flex flex-col pt-4 gap-4">
				<div className="flex justify-between items-end">
					<SearchFilter
						id="searchByDistrictName"
						name="searchByDistrictName"
						searchFilter={searchFilter}
						setSearchFilter={setSearchFilter}
						label="Search"
						placeHolder="Search by district name..."
						className="w-64"
					/>
					<Button
						id="add-district-button"
						className="btn btn-primary h-fit w-fit whitespace-nowrap"
						onClick={() => setAddDistrictFormIsOpen(true)}>
						<span className="flex items-center">
							<PlusIcon className="size-4 mr-2" /> Add District
						</span>
					</Button>
				</div>
				<Loading loading={!districts} />
				{districts && (
					<>
						<DataTable
							value={paginatedDistricts}
							dataKey="districtID"
							emptyMessage="No districts found"
							stripedRows
							sortField={sortField}
							sortOrder={sortOrder}
							onSort={handleSort}
							sortIcon={CustomSortIcon}
							className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default">
							{!config?.isUtahComposeSite && hasClassLinkOrCleverSchool && (
								<Column
									body={cleverOrClassLinkBodyTemplate}
									headerClassName="w-4"
								/>
							)}
							<Column
								header="District Code"
								field="districtCode"
								sortable
								sortField="districtCode"
							/>
							<Column
								header="ID"
								field="externalID"
								sortable
								sortField="externalID"
							/>
							<Column
								header="District Name"
								field="districtName"
								sortable
								sortField="districtName"
							/>
							<Column
								header="Location"
								body={locationBodyTemplate}
								sortable
								sortField="location"
							/>
							<Column
								header="Primary Contact"
								body={contactBodyTemplate}
								sortable
								sortField="contact"
							/>
							<Column
								header="School Count"
								field="schoolCount"
								sortable
								sortField="schoolCount"
							/>
							<Column body={editBodyTemplate} className="!pl-1 !pr-2" />
						</DataTable>
						<SimplePagination
							itemCount={sortedDistricts.length}
							page={page}
							pageSize={pageSize}
							totalCount={districts?.length}
							isFiltered={searchFilter.length > 0}
							forceShow={true}
							onChange={(page) => handlePageChange(page)}
							className={"border-none"}
						/>
					</>
				)}
			</section>
			<AddDistrict
				addDistrictFormIsOpen={addDistrictFormIsOpen}
				setAddDistrictFormIsOpen={setAddDistrictFormIsOpen}
			/>
		</>
	);
}
